import { Chip, Button as HButton, Dropdown, DropdownTrigger, DropdownMenu, DropdownItem } from "@heroui/react";
import { NavLink } from "react-router-dom";
import { StatusBadge, fmtDateTime } from "@/components/common";
import { Package, AirplaneTilt, CalendarBlank, Plus, MagnifyingGlass, DotsThreeVertical, Files, Clock, CheckCircle, Truck, Bell, List, ArrowsDownUp, SquaresFour } from "@phosphor-icons/react";

const NEXT = {
  requested: { label: "Add AWB", action: "awb" },
  awb_received: { label: "Confirm Booking", action: "confirm" },
  awb_assigned: { label: "Awaiting Client", action: "view" },
  confirmed: { label: "View Request", action: "view" },
};

export function DispatchHeader({ onNew }) {
  return (
    <div className="flex items-start justify-between gap-3" data-testid="dispatch-header">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-[#0D1B2A] dark:text-foreground">Dispatch</h1>
        <p className="text-sm text-[#64748B] mt-0.5">Air Cargo Bookings</p>
      </div>
      {onNew && (
        <HButton color="primary" radius="lg" className="bg-[#377DD3] font-semibold h-11 min-w-[44px] px-3 sm:px-4" onPress={onNew} startContent={<Plus size={16} weight="bold" />} data-testid="new-dispatch-request-btn">
          <span className="hidden sm:inline">New Request</span><span className="sm:hidden">New</span>
        </HButton>
      )}
    </div>
  );
}

const KPI = [
  { key: "all", label: "Total Requests", icon: Files, tone: "text-[#377DD3] bg-[#377DD3]/10" },
  { key: "requested", label: "Awaiting AWB", icon: Clock, tone: "text-[#B7791F] bg-[#F5A623]/15" },
  { key: "awb_received", label: "AWB Received", icon: AirplaneTilt, tone: "text-[#245FAF] bg-[#377DD3]/12" },
  { key: "awb_assigned", label: "Awaiting Client", icon: Clock, tone: "text-[#6D28D9] bg-[#7C3AED]/12" },
  { key: "confirmed", label: "Confirmed", icon: CheckCircle, tone: "text-[#18A66A] bg-[#18A66A]/12" },
];

export function DispatchKPIs({ counts, onPick }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="dispatch-kpis">
      {KPI.map((k) => (
        <button key={k.key} type="button" onClick={() => onPick(k.key)} className="hp-card p-4 text-left flex items-center gap-3 hover:border-[#377DD3]/40 hover:shadow-md transition-all min-h-[72px]" data-testid={`dispatch-kpi-${k.key}`}>
          <span className={`h-10 w-10 shrink-0 rounded-xl flex items-center justify-center ${k.tone}`}><k.icon size={20} weight="duotone" /></span>
          <span className="min-w-0">
            <span className="block text-[11px] uppercase tracking-wider font-semibold text-[#64748B] leading-tight">{k.label}</span>
            <span className="block font-heading text-2xl font-extrabold text-[#0D1B2A] dark:text-foreground leading-none mt-1">{counts[k.key] ?? 0}</span>
          </span>
        </button>
      ))}
    </div>
  );
}

const FILTERS = [["all", "All"], ["requested", "Awaiting AWB"], ["awb_received", "AWB Received"], ["awb_assigned", "Awaiting Client"], ["confirmed", "Confirmed"]];

export function DispatchFilters({ q, onQ, status, onStatus, counts, sort, onSort, shown }) {
  return (
    <div className="space-y-3" data-testid="drq-filters">
      <div className="relative">
        <MagnifyingGlass size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#64748B]" />
        <input value={q} onChange={(e) => onQ(e.target.value)} placeholder="Search request #, consignee, order, shipment, AWB…"
          className="h-12 w-full rounded-xl border border-border/70 bg-card pl-11 pr-4 text-base sm:text-sm outline-none focus:border-[#377DD3] focus:ring-2 focus:ring-[#377DD3]/20 transition-colors" data-testid="drq-search-input" />
      </div>
      <div className="flex items-center gap-2">
        <div className="flex gap-2 overflow-x-auto hd-scroll flex-1 min-w-0 pb-0.5" data-testid="drq-filter-chips">
          {FILTERS.map(([k, l]) => {
            const active = status === k;
            return (
              <button key={k} type="button" onClick={() => onStatus(k)} data-testid={`drq-filter-${k}`}
                className={`shrink-0 inline-flex items-center gap-1.5 h-10 rounded-full border px-3.5 text-sm font-medium transition-colors ${active ? "bg-[#377DD3] border-[#377DD3] text-white" : "bg-card border-border/70 text-[#0D1B2A] dark:text-foreground hover:border-[#377DD3]/50"}`}>
                {l}<span className={`inline-flex min-w-[20px] h-5 items-center justify-center rounded-full px-1.5 text-[11px] font-bold ${active ? "bg-white/20" : "bg-muted text-[#64748B]"}`}>{counts[k] ?? 0}</span>
              </button>
            );
          })}
        </div>
        <Dropdown>
          <DropdownTrigger>
            <button type="button" className="shrink-0 inline-flex items-center gap-1.5 h-10 rounded-full border border-border/70 bg-card px-3 text-sm text-[#64748B] hover:text-foreground" data-testid="drq-sort">
              <ArrowsDownUp size={16} /><span className="hidden sm:inline">{sort === "newest" ? "Newest first" : "Oldest first"}</span>
            </button>
          </DropdownTrigger>
          <DropdownMenu aria-label="Sort" selectedKeys={[sort]} selectionMode="single" onAction={(k) => onSort(String(k))}>
            <DropdownItem key="newest" data-testid="drq-sort-newest">Newest first</DropdownItem>
            <DropdownItem key="oldest" data-testid="drq-sort-oldest">Oldest first</DropdownItem>
          </DropdownMenu>
        </Dropdown>
      </div>
      <p className="text-xs text-[#64748B]" data-testid="drq-filter-count">{shown} of {counts.all} requests</p>
    </div>
  );
}

function Links({ r }) {
  const extra = (r.order_numbers?.length || 0) - 1;
  return (
    <span data-testid={`drq-links-${r.request_number}`}>
      {r.order_number || "—"}{extra > 0 && <span className="ml-1 text-[11px] font-semibold text-[#377DD3]">+{extra}</span>}
      {r.shipment_number && <span className="text-[#64748B]"> • {r.shipment_number}</span>}
    </span>
  );
}

export function DispatchRequestCard({ r, onOpen, canAct }) {
  const next = NEXT[r.status] || NEXT.confirmed;
  const act = canAct || r.status === "confirmed" ? next : { label: "View Request", action: "view" };
  return (
    <article className="hp-card p-4 space-y-3" data-testid={`drq-card-${r.request_number}`} onClick={() => onOpen(r, "view")}>
      <div className="flex items-center justify-between gap-2">
        <span className="font-heading font-extrabold text-[#0D1B2A] dark:text-foreground">{r.request_number}</span>
        <StatusBadge status={r.status} />
      </div>
      <p className="text-lg font-semibold leading-tight text-[#0D1B2A] dark:text-foreground truncate">{r.consignee}</p>
      <p className="text-sm text-[#64748B]"><Links r={r} /></p>
      <div className="grid grid-cols-3 gap-2 text-xs text-[#0D1B2A] dark:text-foreground">
        <span className="flex items-center gap-1.5 min-w-0"><Package size={16} className="text-[#377DD3] shrink-0" />{r.boxes} {r.boxes === 1 ? "Box" : "Boxes"}</span>
        <span className="flex items-center gap-1.5 min-w-0 truncate"><AirplaneTilt size={16} className="text-[#377DD3] shrink-0" /><span className="truncate">{r.awb_number || (r.awb_documents?.length ? `${r.awb_documents.length} file(s)` : "—")}</span></span>
        <span className="flex items-center gap-1.5 min-w-0 truncate"><CalendarBlank size={16} className="text-[#377DD3] shrink-0" /><span className="truncate">{fmtDateTime(r.created_at)}</span></span>
      </div>
      <button type="button" onClick={(e) => { e.stopPropagation(); onOpen(r, act.action); }}
        className={`h-12 w-full rounded-xl font-semibold text-sm transition-colors ${act.action === "view" ? "border border-[#377DD3]/40 text-[#245FAF] bg-card hover:bg-[#377DD3]/5" : "bg-[#377DD3] text-white hover:bg-[#245FAF]"}`}
        data-testid={`drq-next-${r.request_number}`}>{act.label}</button>
    </article>
  );
}

export function DispatchTable({ rows, any, onOpen }) {
  return (
    <div className="hp-card overflow-hidden" data-testid="drq-table">
      <div className="max-h-[70vh] overflow-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 bg-card shadow-[0_1px_0_0_hsl(var(--border))]">
            <tr className="text-left text-[11px] uppercase tracking-wider font-semibold text-[#64748B]">
              <th className="px-4 py-3 whitespace-nowrap">Request #</th><th className="px-4 py-3">Consignee</th><th className="px-4 py-3">Order / Shipment</th>
              <th className="px-4 py-3 text-right">Boxes</th><th className="px-4 py-3">AWB</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Requested</th><th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={8} className="px-4 py-12 text-center text-[#64748B]">{any ? "No requests match your filter." : "No booking requests yet."}</td></tr>
            ) : rows.map((r) => {
              const next = NEXT[r.status] || NEXT.confirmed;
              return (
                <tr key={r.id} className="border-t border-border/60 hover:bg-[#377DD3]/[0.04] cursor-pointer transition-colors" onClick={() => onOpen(r, "view")} data-testid={`drq-row-${r.request_number}`}>
                  <td className="px-4 py-3 font-heading font-extrabold whitespace-nowrap text-[#0D1B2A] dark:text-foreground">{r.request_number}</td>
                  <td className="px-4 py-3"><span className="font-medium">{r.consignee}</span>{r.phone && <span className="block text-xs text-[#64748B]">{r.phone}</span>}</td>
                  <td className="px-4 py-3 whitespace-nowrap"><Links r={r} /></td>
                  <td className="px-4 py-3 text-right">{r.boxes}</td>
                  <td className="px-4 py-3 font-mono text-xs">{r.awb_number || (r.awb_documents?.length ? `${r.awb_documents.length} file(s)` : "—")}</td>
                  <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                  <td className="px-4 py-3 text-[#64748B] whitespace-nowrap">{fmtDateTime(r.created_at)}</td>
                  <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                    <Dropdown>
                      <DropdownTrigger><button type="button" className="h-9 w-9 inline-flex items-center justify-center rounded-lg hover:bg-muted" aria-label="Actions" data-testid={`drq-open-${r.request_number}`}><DotsThreeVertical size={18} weight="bold" /></button></DropdownTrigger>
                      <DropdownMenu aria-label="Row actions" onAction={(k) => onOpen(r, String(k))}>
                        <DropdownItem key="view">View details</DropdownItem>
                        {r.status !== "confirmed" ? <DropdownItem key="awb">Add / edit AWB</DropdownItem> : null}
                        {r.status === "awb_received" || r.status === "awb_assigned" ? <DropdownItem key="confirm" className="text-[#18A66A]">Confirm booking</DropdownItem> : null}
                        {next.action !== "view" ? <DropdownItem key={next.action}>{next.label}</DropdownItem> : null}
                      </DropdownMenu>
                    </Dropdown>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function EmptyRequests({ any }) {
  return (
    <div className="hp-card p-10 text-center" data-testid="drq-empty">
      <AirplaneTilt size={36} weight="duotone" className="mx-auto text-[#377DD3]" />
      <p className="mt-3 font-semibold text-[#0D1B2A] dark:text-foreground">{any ? "No requests match your filter" : "No booking requests yet"}</p>
      <p className="text-sm text-[#64748B] mt-1">{any ? "Try another status or clear the search." : "New air-cargo booking requests will appear here."}</p>
    </div>
  );
}

export function MobileBottomNavigation({ isDispatch, isTeam, isClientOps, onMenu, onNotifications }) {
  const items = isDispatch
    ? [{ to: "/dispatch-requests", label: "Dispatch", icon: AirplaneTilt }, { to: "/dispatch", label: "Jobs", icon: Truck }]
    : isClientOps
      ? [{ to: "/portal", label: "Portal", icon: SquaresFour }]
      : [{ to: "/dashboard", label: "Home", icon: SquaresFour }, { to: "/orders", label: "Orders", icon: Package }, { to: "/shipments", label: "Shipments", icon: Truck }];
  const cls = ({ isActive }) => `flex flex-col items-center justify-center gap-0.5 min-w-[56px] h-full text-[11px] font-medium ${isActive ? "text-[#377DD3]" : "text-[#64748B]"}`;
  return (
    <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 border-t border-border/70 bg-card/95 backdrop-blur-md" style={{ paddingBottom: "env(safe-area-inset-bottom)" }} data-testid="mobile-bottom-nav">
      <div className="flex items-stretch justify-around h-16">
        {items.map((it) => <NavLink key={it.to} to={it.to} className={cls} data-testid={`mobile-nav-${it.label.toLowerCase()}`}><it.icon size={22} weight="duotone" /><span>{it.label}</span></NavLink>)}
        <button type="button" onClick={onNotifications} className="flex flex-col items-center justify-center gap-0.5 min-w-[56px] text-[11px] font-medium text-[#64748B]" data-testid="mobile-nav-notifications"><Bell size={22} weight="duotone" /><span>Alerts</span></button>
        <button type="button" onClick={onMenu} className="flex flex-col items-center justify-center gap-0.5 min-w-[56px] text-[11px] font-medium text-[#64748B]" data-testid="mobile-nav-menu"><List size={22} /><span>Menu</span></button>
      </div>
    </nav>
  );
}
